import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ReportType, ShapeType } from '../../reportsTypes';
import { StepConfig, useStepper } from '@ais-ui/ui-core';
import { DateRange } from 'react-day-picker';
import {
  GeoJsonDictionary,
  ReportFilterType,
  ShapeDictionaryType,
} from '../../utils';
import { UseFormReturn } from 'react-hook-form';
import { useDatabaseRange } from '@ais-ui/api';
import { Ship } from '../utils/createDynamicFilterZodSchema';

interface CreateReportContextProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  chosenReportType: ReportType | null | undefined;
  setChosenReportType: React.Dispatch<
    React.SetStateAction<ReportType | null | undefined>
  >;
  steps: StepConfig[];
  setSteps: React.Dispatch<React.SetStateAction<StepConfig[]>>;
  reportTemplateName: string;
  setReportTemplateName: React.Dispatch<React.SetStateAction<string>>;
  firstReportName: string;
  setFirstReportName: React.Dispatch<React.SetStateAction<string>>;
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  filter: ReportFilterType | null;
  setFilter: React.Dispatch<React.SetStateAction<ReportFilterType | null>>;
  shapeDictionary: ShapeDictionaryType;
  setShapeDictionary: React.Dispatch<React.SetStateAction<ShapeDictionaryType>>;
  miscellaneous: Record<string, any>;
  setMiscellaneous: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  stepperProps: ReturnType<typeof useStepper>;
  currentStep: StepConfig | null;
  setCurrentStep: React.Dispatch<React.SetStateAction<StepConfig | null>>;
  resetSteps: () => void;
  instantShape: boolean;
  setInstantShape: React.Dispatch<React.SetStateAction<boolean>>;
  shapeTab: string;
  setShapeTab: React.Dispatch<React.SetStateAction<string>>;
  instantShapeData: GeoJsonDictionary;
  setInstantShapeData: React.Dispatch<React.SetStateAction<GeoJsonDictionary>>;
  activeStep: number;
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  ships: Ship[];
  setShips: React.Dispatch<React.SetStateAction<Ship[]>>;
  shipAttribute: 'colorBy' | 'filterBy';
  setShipAttribute: React.Dispatch<
    React.SetStateAction<'colorBy' | 'filterBy'>
  >;
}

const CreateReportContext = createContext<CreateReportContextProps | null>(
  null
);

export const useCreateReportContext = () => {
  const context = useContext(CreateReportContext);
  if (!context) {
    throw new Error(
      'useCreateReportContext must be used within a CreateReportProvider'
    );
  }
  return context;
};

interface CreateReportProviderProps {
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  children: ReactNode;
}

const baseSteps = [
  { label: 'Name' },
  { label: 'Period' },
] satisfies StepConfig[];

const getDefaultDateRange = (): DateRange => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return {
    from: new Date(yesterday.getTime() - 60 * 60 * 1000),
    to: new Date(yesterday.getTime()),
  };
};

export const CreateReportProvider: React.FC<CreateReportProviderProps> = ({
  startDatetimeThreshold,
  endDatetimeThreshold,
  children,
}) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const { data: databaseRange } = useDatabaseRange();

  // Use database range values as fallbacks for undefined thresholds
  const effectiveStartDatetimeThreshold = useMemo(
    () =>
      startDatetimeThreshold !== undefined
        ? startDatetimeThreshold
        : databaseRange?.database.begin,
    [startDatetimeThreshold, databaseRange]
  );

  const effectiveEndDatetimeThreshold = useMemo(
    () =>
      endDatetimeThreshold !== undefined
        ? endDatetimeThreshold
        : databaseRange?.database.end,
    [endDatetimeThreshold, databaseRange]
  );

  const [chosenReportType, setChosenReportType] = useState<
    ReportType | null | undefined
  >(null);
  const [steps, setSteps] = useState<StepConfig[]>(baseSteps);

  const [reportTemplateName, setReportTemplateName] = useState<string>('');
  const [firstReportName, setFirstReportName] = useState<string>('');

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getDefaultDateRange()
  );
  const [shapeTab, setShapeTab] = useState<string>('shape');
  const [filter, setFilter] = useState<ReportFilterType | null>(null);
  const [ships, setShips] = useState<Ship[]>([]);
  const [shipAttribute, setShipAttribute] = useState<'colorBy' | 'filterBy'>(
    'filterBy'
  );

  // The shapeDictionary is used to store the data which is used in the shapes
  // step in the context of reports.
  const [shapeDictionary, setShapeDictionary] =
    useState<ShapeDictionaryType>(null);
  const [instantShapeData, setInstantShapeData] = useState<GeoJsonDictionary>(
    {}
  );
  // Here we are listening for the instantShape event from useDrawFromDialog.ts
  useEffect(() => {
    const handleInstantShapeEvent = (e: any) => {
      setInstantShapeData((prevData) => ({ ...prevData, ...e.detail }));
    };
    window.addEventListener('instantShape', handleInstantShapeEvent);

    return () => {
      window.removeEventListener('instantShape', handleInstantShapeEvent);
    };
  }, [shapeTab]);

  const [miscellaneous, setMiscellaneous] = useState<Record<string, any>>({});

  const [instantShape, setInstantShape] = useState(false);

  const stepperProps = useStepper({
    initialStep: 0,
    steps,
  });

  const activeStep = useMemo(
    () => stepperProps.activeStep,
    [stepperProps.activeStep]
  );

  const [currentStep, setCurrentStep] = useState<StepConfig | null>(null);
  const [shapeForm, setShapeForm] = useState<UseFormReturn<any> | null>(null);

  const resetSteps = useCallback(() => {
    setChosenReportType(null);

    setSteps([]);

    setReportTemplateName('');
    setFirstReportName('');

    setDateRange(getDefaultDateRange());

    setFilter(null);
    setShapeDictionary(null);
    setMiscellaneous({});
    setShapeForm(null);

    stepperProps.resetSteps();
  }, [
    setChosenReportType,
    setSteps,
    setReportTemplateName,
    setFirstReportName,
    setDateRange,
    setFilter,
    setShapeDictionary,
    setMiscellaneous,
    stepperProps,
  ]);

  useEffect(() => {
    const tempSteps: StepConfig[] = [...baseSteps];
    if (!chosenReportType) {
      setSteps([]);
      return;
    }

    if (chosenReportType.ships) {
      tempSteps.push({
        label: 'Ships',
      });
    }

    if (chosenReportType.filter) {
      tempSteps.push({
        label: 'Filter',
      });
    }

    if (chosenReportType.shape) {
      tempSteps.push({
        label: 'Shape',
      });
    }

    if (chosenReportType.miscellaneous) {
      tempSteps.push({
        label: 'Miscellaneous',
      });
    }

    setSteps(tempSteps);
  }, [chosenReportType]);

  useEffect(() => {
    if (steps.length === 0) {
      setCurrentStep(null);
      return;
    }
    setCurrentStep(steps[stepperProps.activeStep]);
  }, [stepperProps, steps]);

  const value = {
    showDialog,
    setShowDialog,
    chosenReportType,
    setChosenReportType,
    steps,
    setSteps,
    reportTemplateName,
    setReportTemplateName,
    firstReportName,
    setFirstReportName,
    dateRange,
    setDateRange,
    filter,
    setFilter,
    shapeDictionary,
    setShapeDictionary,
    miscellaneous,
    setMiscellaneous,
    stepperProps,
    currentStep,
    setCurrentStep,
    resetSteps,
    instantShape,
    setInstantShape,
    shapeForm,
    setShapeForm,
    shapeTab,
    setShapeTab,
    instantShapeData,
    setInstantShapeData,
    activeStep,
    startDatetimeThreshold: effectiveStartDatetimeThreshold,
    endDatetimeThreshold: effectiveEndDatetimeThreshold,
    ships,
    setShips,
    shipAttribute,
    setShipAttribute,
  };

  return (
    <CreateReportContext.Provider value={value}>
      {children}
    </CreateReportContext.Provider>
  );
};
